import { ButtonBase, useTheme } from "@mui/material";
import { createLink } from "@tanstack/react-router";
import type { RouteTarget } from "@/routing/routeTarget";
import ImgHelper from "./ImgHelper";

/**
 * `ButtonBase` renders an anchor once `createLink` hands it an `href`, so a
 * logo given a destination is a real link: right-click offers "Open link in a
 * new tab" and middle-click opens the dashboard, where a button offered only
 * the image (R-2238). The router still handles a plain click in place.
 */
const LogoLink = createLink(ButtonBase);

interface LogoProps {
  width?: number | string;
  /**
   * The full logo with the wordmark, or the symbol alone: the three bars, in
   * the brand colour in both themes, drawn 22px tall as the sidebar's icon
   * rail has it (R-2295).
   */
  variant?: "full" | "symbol";
  /** Where the logo leads. Without it the logo is a button, or decoration. */
  to?: RouteTarget["to"];
  onClick?: () => void;
  "data-testid"?: string;
}

const SYMBOL_HEIGHT = 22;

export default function WebshareLogo(props: LogoProps) {
  const {
    width,
    variant = "full",
    to,
    onClick,
    "data-testid": dataTestId,
  } = props;
  const theme = useTheme();
  const sx = variant === "symbol" ? {} : { width: width || 240 };
  const image =
    variant === "symbol" ? (
      <ImgHelper
        sx={{ height: SYMBOL_HEIGHT }}
        alt="Webshare Home"
        src="/logoSymbol.svg"
      />
    ) : (
      /* Scaled to the width asked for: the dark file is drawn smaller (180px)
       than the light one (241px), so left at its own size it shrinks. */
      <ImgHelper
        sx={{ width: "100%" }}
        alt="Webshare Home"
        src={
          theme.palette.mode === "light"
            ? "/logoWithText.svg"
            : "/darkThemeLogoWithText.svg"
        }
      />
    );

  if (to) {
    return (
      <LogoLink to={to} sx={sx} onClick={onClick} data-testid={dataTestId}>
        {image}
      </LogoLink>
    );
  }

  return (
    <ButtonBase sx={sx} onClick={onClick} data-testid={dataTestId}>
      {image}
    </ButtonBase>
  );
}
